import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Suspense } from "react";
import { PageLoader } from "@/components/PageLoader";
import {
    Home, Heart, BookOpen, MessageCircle, LifeBuoy, LogOut,
    ChevronDown, FileCheck2, Shield, ScrollText, UserCog, Info, FolderOpen,
    Grid3x3, Layers, CheckSquare, Smile, CalendarDays, Gamepad2, TrendingUp,
    Target, Scale, ListChecks, Compass, BookMarked, ChevronRight, Zap, FileText, Pill, ArrowLeft, ClipboardPlus, Flag, Accessibility, Phone, Package,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PhoenixWordmark } from "@/components/PhoenixLogo";
import InmateOnboardingWizard from "@/components/InmateOnboardingWizard";
import ChatbotWidget from "@/components/ChatbotWidget";
import { api } from "@/lib/api";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { TIDS } from "@/constants/testIds";
import { SubscriptionLockScreen } from "@/components/SubscriptionLock";
import { MyMedicationsModal } from "@/components/MyMedicationsModal";
import { EmergencyContactModal } from "@/components/EmergencyContactModal";
import { InmateAdaDialog } from "@/components/inmate/InmateAdaDialog";

const navItems = [
    { to: "/inmate", icon: Home, label: "Home", tid: TIDS.inmateNavHome, exact: true },
    { to: "/inmate/mood", icon: Heart, label: "Mood", tid: TIDS.inmateNavMood },
    { to: "/inmate/journal", icon: BookOpen, label: "Journal", tid: TIDS.inmateNavJournal },
    { to: "/inmate/chat", icon: MessageCircle, label: "Blue", tid: TIDS.inmateNavChat },
    { to: "/inmate/crisis", icon: LifeBuoy, label: "Crisis", tid: TIDS.inmateNavCrisis },
];

// Left-sidebar section links — scroll to the matching dashboard row.
const SECTION_NAV = [
    { id: "checkin", label: "Daily Check-In", icon: Heart },
    { id: "wellness", label: "Wellness", icon: Smile },
    { id: "cope", label: "Learning to Cope", icon: Layers },
    { id: "reentry", label: "Getting Ready for Reentry", icon: Compass },
    { id: "resources", label: "Resource Library", icon: BookMarked },
];

// Surfaces reachable from the bottom-nav "More" sheet (everything not on the 5 primary tabs).
const MORE_GROUPS = [
    {
        label: "Learn & Practice",
        items: [
            { to: "/inmate/challenges", icon: Zap, title: "Skills Lab — Challenges", iconBg: "bg-phoenix-orange" },
            { to: "/inmate/skills", icon: Layers, title: "Skills library", iconBg: "bg-indigo-500" },
            { to: "/inmate/habits", icon: CheckSquare, title: "Daily habits", iconBg: "bg-sky-500" },
            { to: "/inmate/gratitude", icon: Smile, title: "Gratitude", iconBg: "bg-amber-500" },
        ],
    },
    {
        label: "Wellness",
        items: [
            { to: "/inmate/sick-call", icon: ClipboardPlus, title: "Request health care", iconBg: "bg-teal-600" },
            { to: "/inmate/appointments", icon: CalendarDays, title: "My appointments", iconBg: "bg-cyan-600" },
            { to: "/inmate/games", icon: Gamepad2, title: "Games & activities", iconBg: "bg-emerald-500" },
            { to: "/inmate/insights", icon: TrendingUp, title: "Insights", iconBg: "bg-purple-500" },
        ],
    },
    {
        label: "Growth & Goals",
        items: [
            { to: "/inmate/goals", icon: Target, title: "My goals", iconBg: "bg-success" },
            { to: "/inmate/accountability", icon: Scale, title: "Accountability & repair", iconBg: "bg-rose-500" },
            { to: "/inmate/restoration", icon: Heart, title: "Restoration plan", iconBg: "bg-pink-500" },
        ],
    },
    {
        label: "Reentry & Resources",
        items: [
            { to: "/inmate/pre-release", icon: Compass, title: "Pre-release program", iconBg: "bg-phoenix-orange" },
            { to: "/inmate/reentry-plan", icon: Compass, title: "My reentry plan", iconBg: "bg-phoenix-navy" },
            { to: "/inmate/resources", icon: BookMarked, title: "Resources", iconBg: "bg-stone-600" },
        ],
    },
];

export default function InmateLayout() {
    const { user, fetchMe, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isHome = location.pathname === "/inmate" || location.pathname === "/inmate/";
    const [medsOpen, setMedsOpen] = useState(false);
    const [contactOpen, setContactOpen] = useState(false);
    const [adaOpen, setAdaOpen] = useState(false);
    const firstName = user?.inmate?.first_name || user?.full_name?.split(" ")[0] || "Friend";
    const inmate = user?.inmate || null;
    const institution = user?.institution || null;

    // ── Unskippable onboarding wizard gating ──
    // The wizard internally sequences welcome → consents → HIPAA policy → treatment →
    // care-team authorization → self-assessment → PHQ-9 → success. We only decide here
    // WHETHER to mount it. `roi_active` (external medical-record release) is intentionally
    // NOT a gate — it lives in the "Medical Records" menu page and is optional.
    const needsWelcome = !!user && !user.has_seen_welcome;
    const needsConsent = !!user && !user.consent_accepted;
    const needsHipaa = !!user && !user.hipaa_acknowledged;
    const needsPatientRights = !!user && !user.patient_rights_acknowledged;
    const consentCleared = !!user && user.consent_accepted && user.hipaa_acknowledged;

    const [assessStatus, setAssessStatus] = useState(null);
    const [moreOpen, setMoreOpen] = useState(false);
    useEffect(() => {
        if (consentCleared && !assessStatus) {
            api.get("/inmate/assessments/status")
                .then((r) => setAssessStatus(r.data))
                .catch(() => setAssessStatus({ self_assessment_done: true, phq9_baseline_done: true }));
        }
    }, [consentCleared, assessStatus]);
    const needsAssess = !!assessStatus && !(assessStatus.self_assessment_done && assessStatus.phq9_baseline_done);

    const gated = needsWelcome || needsConsent || needsHipaa || needsPatientRights || needsAssess;

    // Track foreground time-on-screen once the inmate is actively using the app.
    useEngagementTracker(!!user && !gated);

    // HIPAA auto-logoff for shared facility tablets — sign out after 15 min idle.
    useIdleLogout(!!user);

    const handleLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    // Scroll to a dashboard section from the left nav (navigating Home first if needed).
    const goToSection = (id) => {
        const scroll = () => document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (window.location.pathname !== "/inmate") {
            navigate("/inmate");
            setTimeout(scroll, 250);
        } else {
            scroll();
        }
    };

    // Phase 4C — facility subscription lockout
    if (user?.subscription?.locked) {
        return <SubscriptionLockScreen onLogout={handleLogout} audience="inmate" />;
    }

    // Inactive status (released/transferred) — 90-day grace period banner
    const inmateStatus = user?.inmate?.status;
    let graceDaysLeft = null;
    if (inmateStatus && inmateStatus !== "active") {
        const base = user.inmate.release_date || user.inmate.status_changed_at;
        if (base) {
            const elapsed = Math.floor((Date.now() - new Date(base).getTime()) / 86400000);
            graceDaysLeft = Math.max(0, 90 - elapsed);
        }
    }

    return (
        <div className="min-h-screen bg-phoenix-mesh pb-32">
            <header className="sticky top-0 z-30 backdrop-blur-md bg-white/70 border-b border-border/60">
                <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
                    <PhoenixWordmark />

                    {/* User menu */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="inmate-user-menu"
                                className="inline-flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-secondary/60 transition-colors"
                            >
                                <span className="w-8 h-8 rounded-full bg-phoenix-navy text-white text-sm font-semibold flex items-center justify-center">
                                    {firstName[0]?.toUpperCase()}
                                </span>
                                <span className="hidden sm:inline text-sm font-medium text-phoenix-navy">{firstName}</span>
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                            <DropdownMenuLabel>
                                <div className="font-display font-semibold text-phoenix-navy">{user?.full_name || firstName}</div>
                                {inmate?.inmate_number && (
                                    <div className="text-xs text-muted-foreground font-mono mt-0.5">#{inmate.inmate_number}</div>
                                )}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                data-testid="inmate-menu-account"
                                onClick={() => navigate("/inmate/account")}
                            >
                                <UserCog className="w-4 h-4" /> My Account
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-medications"
                                onClick={() => setMedsOpen(true)}
                            >
                                <Pill className="w-4 h-4" /> My Medications
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-emergency-contact"
                                onClick={() => setContactOpen(true)}
                            >
                                <Phone className="w-4 h-4" /> Emergency Contact
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-consents"
                                onClick={() => navigate("/inmate/consents")}
                            >
                                <FileCheck2 className="w-4 h-4" /> My Consents
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-medical-records"
                                onClick={() => navigate("/inmate/medical-records")}
                            >
                                <FolderOpen className="w-4 h-4" /> Medical Records
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-property-inventory"
                                onClick={() => navigate("/inmate/property-inventory")}
                            >
                                <Package className="w-4 h-4" /> Personal Inventory
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-record-request"
                                onClick={() => navigate("/inmate/record-requests")}
                            >
                                <FileText className="w-4 h-4" /> Request my records
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-ada"
                                onClick={() => setAdaOpen(true)}
                            >
                                <Accessibility className="w-4 h-4" /> Disability Accommodation
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-grievances"
                                onClick={() => navigate("/inmate/grievances")}
                            >
                                <Flag className="w-4 h-4" /> Grievances & Safety Reports
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-privacy"
                                onClick={() => window.open("/privacy", "_blank", "noopener")}
                            >
                                <Shield className="w-4 h-4" /> Privacy Policy
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                data-testid="inmate-menu-terms"
                                onClick={() => window.open("/terms", "_blank", "noopener")}
                            >
                                <ScrollText className="w-4 h-4" /> Terms of Service
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                data-testid={TIDS.inmateLogout}
                                onClick={handleLogout}
                                className="text-destructive focus:text-destructive"
                            >
                                <LogOut className="w-4 h-4" /> Sign out
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <MyMedicationsModal open={medsOpen} onOpenChange={setMedsOpen} />
            <EmergencyContactModal open={contactOpen} onOpenChange={setContactOpen} />
            <InmateAdaDialog open={adaOpen} onClose={() => setAdaOpen(false)} />
            {/* Inactive status — 90-day grace banner */}
            {inmateStatus && inmateStatus !== "active" && (
                <div data-testid="inactive-status-banner" className="bg-amber-50 border-b border-amber-200 px-5 py-3">
                    <div className="max-w-5xl mx-auto flex items-start gap-2.5 text-sm text-amber-900">
                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                            {inmateStatus === "released"
                                ? "You've been released — congratulations on this next chapter."
                                : "Our records show you've been transferred."}{" "}
                            {graceDaysLeft === null
                                ? "Your Blue Phoenix access continues during your 90-day transition period."
                                : graceDaysLeft > 0
                                    ? `Your Blue Phoenix access continues for ${graceDaysLeft} more day${graceDaysLeft === 1 ? "" : "s"} to support your transition.`
                                    : "Your 90-day transition period has ended. Reach out to your case manager about continued care."}
                        </span>
                    </div>
                </div>
            )}

            <div className="lg:flex lg:max-w-6xl lg:mx-auto lg:gap-6 lg:px-5">
                {/* Left sidebar — desktop/tablet only */}
                {!gated && (
                    <aside className="hidden lg:block w-60 shrink-0 pt-6" aria-label="Sections">
                        <div className="sticky top-24 space-y-1">
                            <NavLink to="/inmate" end data-testid="sidenav-home"
                                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${isActive ? "bg-phoenix-navy text-white" : "text-phoenix-navy hover:bg-secondary/60"}`}>
                                <Home className="w-4 h-4" /> Home
                            </NavLink>
                            <div className="pt-2 pb-1 px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Browse</div>
                            {SECTION_NAV.map(({ id, label, icon: Icon }) => (
                                <button key={id} type="button" data-testid={`sidenav-${id}`} onClick={() => goToSection(id)}
                                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-phoenix-navy/90 hover:bg-secondary/60 transition-colors text-left">
                                    <Icon className="w-4 h-4 text-phoenix-navy/60 shrink-0" /> {label}
                                </button>
                            ))}
                            <div className="pt-2 mt-1 border-t border-border/60 space-y-1">
                                <NavLink to="/inmate/chat" data-testid="sidenav-chat"
                                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-phoenix-navy/90 hover:bg-secondary/60 transition-colors">
                                    <MessageCircle className="w-4 h-4 text-phoenix-navy/60" /> Talk with Blue
                                </NavLink>
                                <NavLink to="/inmate/crisis" data-testid="sidenav-crisis"
                                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold text-phoenix-orange hover:bg-orange-50 transition-colors">
                                    <LifeBuoy className="w-4 h-4" /> I need help now
                                </NavLink>
                            </div>
                        </div>
                    </aside>
                )}

                <main className="flex-1 w-full max-w-3xl mx-auto px-5 lg:px-0 pt-6 pb-12" aria-hidden={gated || undefined}>
                    {!isHome && !gated && (
                        <button type="button" onClick={() => navigate(-1)} data-testid="inmate-back-btn"
                            className="inline-flex items-center gap-1.5 mb-4 text-sm font-semibold text-phoenix-navy/80 hover:text-phoenix-navy transition-colors">
                            <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                    )}
                    <Suspense fallback={<PageLoader />}>
                        <Outlet context={{ firstName, inmate, institution }} />
                    </Suspense>
                </main>
            </div>

            {/* Floating Blue access */}
            {!gated && <ChatbotWidget />}

            {/* Bottom nav — mobile only (desktop uses the left sidebar) */}
            <nav
                className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-lg border-t border-border/60 shadow-phoenix-deep"
                aria-label="Primary"
            >
                <div className="max-w-3xl mx-auto px-3 py-2 grid grid-cols-6 gap-1">
                    {navItems.map(({ to, icon: Icon, label, tid, exact }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={exact}
                            data-testid={tid}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-2xl min-h-[56px] transition-all ${
                                    isActive
                                        ? "bg-phoenix-navy text-white shadow-phoenix-card"
                                        : "text-muted-foreground hover:text-phoenix-navy hover:bg-secondary/60"
                                }`
                            }
                        >
                            <Icon className="w-5 h-5" />
                            <span className="text-[11px] font-medium">{label}</span>
                        </NavLink>
                    ))}
                    <button
                        type="button"
                        data-testid="inmate-nav-more"
                        onClick={() => setMoreOpen(true)}
                        className="flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-2xl min-h-[56px] transition-all text-muted-foreground hover:text-phoenix-navy hover:bg-secondary/60"
                    >
                        <Grid3x3 className="w-5 h-5" />
                        <span className="text-[11px] font-medium">More</span>
                    </button>
                </div>
            </nav>

            {/* "More" surfaces — bottom sheet */}
            <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                <SheetContent side="bottom" data-testid="inmate-more-sheet" className="max-w-3xl mx-auto rounded-t-3xl max-h-[80vh] overflow-y-auto">
                    <SheetHeader className="text-left">
                        <SheetTitle className="font-display text-phoenix-navy">Explore</SheetTitle>
                    </SheetHeader>
                    <div className="space-y-5 pt-3 pb-2">
                        {MORE_GROUPS.map((group) => (
                            <div key={group.label}>
                                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{group.label}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {group.items.map(({ to, icon: Icon, title, iconBg }) => (
                                        <button
                                            key={to}
                                            type="button"
                                            data-testid={`more-link-${to.split("/").pop()}`}
                                            onClick={() => { setMoreOpen(false); navigate(to); }}
                                            className="group flex items-center gap-3 px-3 py-2.5 rounded-2xl border border-border/60 bg-white hover:bg-secondary/50 hover:-translate-y-0.5 transition-all text-left"
                                        >
                                            <span className={`w-9 h-9 rounded-xl ${iconBg} text-white flex items-center justify-center shrink-0`}>
                                                <Icon className="w-4 h-4" />
                                            </span>
                                            <span className="flex-1 text-sm font-medium text-phoenix-navy">{title}</span>
                                            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Unskippable onboarding wizard */}
            {gated && (
                <InmateOnboardingWizard
                    firstName={firstName}
                    user={user}
                    onComplete={() => fetchMe()}
                />
            )}
        </div>
    );
}
